import api from './api';
import { getPlanningDeviceId } from './planningService';

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  tenantId: string;
  tenantName: string;
  /** tenants.tenant_type: INDIVIDUAL | TENANT | CONSULTANT | PORTFOLIO | … (see pePortfolioService.ts header for glossary) */
  tenantType?: string;
  /** True when user is tenant administrator for their tenant */
  isTenantAdmin?: boolean;
  role: string;
  businessHierarchyId?: string;
  businessHierarchyName?: string;
  functionalHierarchyId?: string;
  functionalHierarchyName?: string;
  geographicHierarchyId?: string;
  geographicHierarchyName?: string;
  profileImageUrl?: string;
}

export interface PlanAttempt {
  id: string;
  billingPlanId: string | null;
}

export interface AuthResponse {
  token: string;
  user: User;
  requirePasswordChange?: boolean;
  planAttempt?: PlanAttempt | null;
}

export const authService = {
  async login(credentials: LoginCredentials): Promise<AuthResponse> {
        const response = await api.post('/auth/login', credentials, {
          headers: { 'X-Device-Id': getPlanningDeviceId() }
        });
        
    // Store the token in localStorage
    if (response.data.data.token) {
      this.setToken(response.data.data.token);
          }
    
    return response.data.data;
  },

  async logout(): Promise<void> {
    await api.post('/auth/logout');
    localStorage.removeItem('auth_token');
  },

  async getCurrentUser(): Promise<User> {
    const response = await api.get('/auth/me');
    return response.data.data;
  },

  async refreshToken(): Promise<{ token: string }> {
    const response = await api.post('/auth/refresh');
    return response.data.data;
  },

  async requestPasswordReset(email: string): Promise<void> {
        const response = await api.post('/auth/forgot-password', { email });
        return response.data;
  },

  async loginWithToken(token: string): Promise<AuthResponse> {
    const response = await api.post('/auth/login-with-token', { token }, {
      headers: { 'X-Device-Id': getPlanningDeviceId() }
    });
    // Store the token in localStorage
    if (response.data.data.token) {
      this.setToken(response.data.data.token);
    }
    return response.data.data;
  },

  async resetPassword(token: string, newPassword: string): Promise<AuthResponse> {
    const response = await api.post('/auth/reset-password', { token, newPassword });
    const data = response.data?.data || response.data;
    // Persist session so the user lands authenticated on the dashboard
    if (data?.token) {
      this.setToken(data.token);
    }
    return data;
  },

  isAuthenticated(): boolean {
    return !!localStorage.getItem('auth_token');
  },

  getToken(): string | null {
    return localStorage.getItem('auth_token');
  },

  setToken(token: string): void {
    localStorage.setItem('auth_token', token);
  },

  removeToken(): void {
    localStorage.removeItem('auth_token');
  }
}; 